import { useState, useEffect, useRef, useCallback } from 'react';
import tablaPrefsApiService from '../../services/http/tablaPrefsApiService';
import { getItem, STORAGE_KEYS } from '../../services/storage';

const GUARDAR_TRAS_MS = 600;

const emailActual = () => (getItem(STORAGE_KEYS.USER)?.profileObj?.email || '').toLowerCase();
const claveLocal = (tabla) => `tablaPrefs:${emailActual()}:${tabla}`;

const leerLocal = (tabla) => {
  try {
    const crudo = window.localStorage.getItem(claveLocal(tabla));
    return crudo ? JSON.parse(crudo) : {};
  } catch (_err) {
    return {};
  }
};

const escribirLocal = (tabla, prefs) => {
  try {
    window.localStorage.setItem(claveLocal(tabla), JSON.stringify(prefs));
  } catch (_err) {
    // Sin espacio o modo privado: las preferencias solo viven en la BD.
  }
};

/**
 * Preferencias de una tabla por usuario. La BD manda; localStorage es la caché
 * que evita el parpadeo al cargar y el respaldo si el backend no responde.
 * `ready` pasa a true cuando ya se conoce la versión del servidor.
 */
const useTablaPrefs = (tabla) => {
  const [prefs, setPrefs] = useState(() => leerLocal(tabla));
  const [ready, setReady] = useState(false);
  const prefsRef = useRef(prefs);
  const timerRef = useRef(null);
  const tocadoRef = useRef(false);
  const pendienteRef = useRef(false);

  useEffect(() => {
    let vivo = true;
    tablaPrefsApiService
      .obtener(tabla)
      .then((res) => {
        if (!vivo || tocadoRef.current) return;
        if (res?.prefs) {
          prefsRef.current = res.prefs;
          setPrefs(res.prefs);
          escribirLocal(tabla, res.prefs);
        } else if (Object.keys(prefsRef.current).length > 0) {
          // Hay preferencias locales y ninguna en la BD: se suben una vez.
          tablaPrefsApiService.guardar(tabla, prefsRef.current).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => vivo && setReady(true));
    return () => {
      vivo = false;
      // Un cambio pendiente (debounce) se envía al salir: si no, cambiar de pestaña lo perdería.
      if (pendienteRef.current) {
        clearTimeout(timerRef.current);
        tablaPrefsApiService.guardar(tabla, prefsRef.current).catch(() => {});
        pendienteRef.current = false;
      }
    };
  }, [tabla]);

  const guardar = useCallback(
    (siguiente) => {
      escribirLocal(tabla, siguiente);
      clearTimeout(timerRef.current);
      pendienteRef.current = true;
      timerRef.current = setTimeout(() => {
        pendienteRef.current = false;
        tablaPrefsApiService.guardar(tabla, siguiente).catch(() => {});
      }, GUARDAR_TRAS_MS);
    },
    [tabla],
  );

  const actualizar = useCallback(
    (parche) => {
      tocadoRef.current = true;
      const siguiente = { ...prefsRef.current, ...parche };
      prefsRef.current = siguiente;
      setPrefs(siguiente);
      guardar(siguiente);
    },
    [guardar],
  );

  const restablecer = useCallback(() => {
    tocadoRef.current = true;
    prefsRef.current = {};
    setPrefs({});
    clearTimeout(timerRef.current);
    try {
      window.localStorage.removeItem(claveLocal(tabla));
    } catch (_err) {
      // nada que limpiar
    }
    tablaPrefsApiService.borrar(tabla).catch(() => {});
  }, [tabla]);

  return { prefs, ready, actualizar, restablecer };
};

export default useTablaPrefs;
